import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { markActivity, clearActivity } from "@/lib/activity";
import { saveOfflineUser, getOfflineUser, clearOffline, isNetworkError } from "@/lib/offline";

const AuthContext = createContext(null);
export const SYNC_KEY = "vt-auth-sync";
// Avisa a las otras pestañas/ventanas de este navegador que la cuenta cambió.
export const notifyAccountChange = () => { try { localStorage.setItem(SYNC_KEY, String(Date.now())); } catch {} };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = guest, object = user
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me", { offlineFallback: true });
      saveOfflineUser(data);
      setUser(data);
    } catch (e) {
      const cached = isNetworkError(e) && getOfflineUser();
      setUser(cached ? { ...cached, offline: true } : false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth callback, let AuthCallback handle it first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    markActivity();
    saveOfflineUser(data);
    sessionStorage.removeItem("catalog-filters");
    setUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    markActivity();
    saveOfflineUser(data);
    sessionStorage.removeItem("catalog-filters");
    setUser(data);
    return data;
  };

  const signOutLocal = useCallback(() => {
    clearActivity();
    clearOffline();
    sessionStorage.removeItem("catalog-filters");
    localStorage.removeItem("vt-fav-communes");
    window.dispatchEvent(new Event("vt-fav-communes"));
    setUser(false);
  }, []);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    signOutLocal();
    notifyAccountChange();
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, checkAuth, signOutLocal }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
